import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import {
  Card, Button, Badge, Select, Spinner, EmptyState,
  Table, TableHead, TableBody, TableRow, TableCell, Modal
} from '../../components/UI';
import { reportsAPI, usersAPI, pipelineAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlineDownload, HiOutlineUser, HiOutlinePhone,
  HiOutlineClock, HiOutlineTrendingUp, HiOutlineClipboardList,
  HiOutlineEye, HiOutlineCalendar
} from 'react-icons/hi';
import { format } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const DailyWorkReports = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [department, setDepartment] = useState('');
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Fetch pipeline stages for dynamic stage colors
  const { data: stagesData } = useQuery({
    queryKey: ['pipelineStagesConfig'],
    queryFn: async () => {
      try {
        const response = await pipelineAPI.getAll();
        return response.data?.data || [];
      } catch (error) {
        console.error('Pipeline stages error:', error);
        return [];
      }
    },
  });

  // Create stage color map and name map
  const stageColorMap = stagesData?.reduce((acc, stage) => {
    acc[stage.key] = stage.color || '#6B7280';
    return acc;
  }, {}) || {};

  const stageNameMap = stagesData?.reduce((acc, stage) => {
    acc[stage.key] = stage.name || stage.key;
    return acc;
  }, {}) || {};

  // Map old status values to stage keys (for backward compatibility)
  const statusToStageMap = {
    'new': 'new_lead',
    'follow_up': 'in_followup',
    'hot': 'hot',
    'warm': 'warm',
    'cold': 'cold',
    'converted': 'converted',
    'closed': 'closed',
  };

  // Get color for a lead status/stage
  const getLeadStatusColor = (status) => {
    const stageKey = statusToStageMap[status] || status;
    return stageColorMap[stageKey] || '#6B7280';
  };

  // Get display name for a lead status/stage
  const getLeadStatusName = (status) => {
    const stageKey = statusToStageMap[status] || status;
    return stageNameMap[stageKey] || status?.replace(/_/g, ' ')?.toLowerCase()?.replace(/\b\w/g, l => l.toUpperCase()) || status;
  };

  // Fetch daily work report
  const { data: reportData, isLoading } = useQuery({
    queryKey: ['dailyWorkReport', selectedDate, department],
    queryFn: async () => {
      const params = { date: selectedDate };
      if (department) params.department = department;
      const response = await reportsAPI.getDailyWorkReport(params);
      return response.data?.data || {};
    },
  });

  // Fetch single user report when modal opens
  const { data: userReport, isLoading: userLoading } = useQuery({
    queryKey: ['userDailyReport', selectedUserId, selectedDate],
    queryFn: async () => {
      const response = await reportsAPI.getUserDailyReport(selectedUserId, { date: selectedDate });
      return response.data?.data || {};
    },
    enabled: !!selectedUserId && showUserModal,
  });

  // Fetch departments for filter
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await usersAPI.getAll({ role: 'telecaller' });
      return response.data?.data || [];
    },
  });

  // Get unique departments
  const departments = usersData
    ? [...new Set(usersData.map(u => u.department).filter(Boolean))].map(d => ({ value: d, label: d }))
    : [];

  const summary = reportData?.summary || {};
  const users = reportData?.users || [];

  const handleViewUser = (userId) => {
    setSelectedUserId(userId);
    setShowUserModal(true);
  };

  const handleExport = async () => {
    try {
      toast.loading('Exporting report...');
      const response = await reportsAPI.exportDailyWork({ date: selectedDate });
      const data = response.data?.data || [];

      if (data.length === 0) {
        toast.dismiss();
        toast.error('No data to export');
        return;
      }

      // Create CSV
      const headers = Object.keys(data[0]);
      const csvRows = [
        headers.join(','),
        ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
      ];

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `daily-work-report-${selectedDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      toast.dismiss();
      toast.success('Report exported successfully');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to export report');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      present: 'success',
      late: 'warning',
      absent: 'danger',
      half_day: 'info',
      early_leave: 'default'
    };
    return variants[status] || 'default';
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatWorkingHours = (hours) => {
    if (!hours) return '0h 0m';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Daily Work Reports</h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-0.5 sm:mt-1">
              Track daily performance of all telecallers
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg sm:rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-0 focus:outline-none"
              max={format(new Date(), 'yyyy-MM-dd')}
            />
            {departments.length > 0 && (
              <Select
                options={[{ value: '', label: 'All Depts' }, ...departments]}
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="w-28 sm:w-32"
              />
            )}
            <Button
              variant="outline"
              size="sm"
              icon={<HiOutlineDownload className="w-4 h-4" />}
              onClick={handleExport}
            >
              <span className="hidden sm:inline">Export</span>
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 lg:gap-4">
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center">
            <div className="text-base sm:text-lg lg:text-2xl font-bold text-gray-900">{summary.totalUsers || 0}</div>
            <div className="text-xs lg:text-sm text-gray-500">Telecallers</div>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-green-50">
            <div className="text-base sm:text-lg lg:text-2xl font-bold text-green-600">{summary.present || 0}</div>
            <div className="text-xs lg:text-sm text-green-600">Present</div>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-red-50">
            <div className="text-base sm:text-lg lg:text-2xl font-bold text-red-600">{summary.absent || 0}</div>
            <div className="text-xs lg:text-sm text-red-600">Absent</div>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-yellow-50">
            <div className="text-base sm:text-lg lg:text-2xl font-bold text-yellow-600">{summary.late || 0}</div>
            <div className="text-xs lg:text-sm text-yellow-600">Late</div>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-primary-50 col-span-2 sm:col-span-1">
            <div className="text-base sm:text-lg lg:text-2xl font-bold text-primary-600">{summary.totalCalls || 0}</div>
            <div className="text-xs lg:text-sm text-primary-600">Total Calls</div>
          </Card>
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
          <Card padding="p-2.5 sm:p-3 lg:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <HiOutlinePhone className="w-4 h-4 lg:w-5 lg:h-5 text-green-600" />
              </div>
              <div className="min-w-0">
                <div className="text-sm sm:text-base lg:text-lg font-bold text-gray-900">{summary.totalConnected || 0}</div>
                <div className="text-xs text-gray-500">Connected</div>
              </div>
            </div>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <HiOutlineClipboardList className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600" />
              </div>
              <div className="min-w-0">
                <div className="text-sm sm:text-base lg:text-lg font-bold text-gray-900">{summary.totalLeadsWorked || 0}</div>
                <div className="text-xs text-gray-500">Leads</div>
              </div>
            </div>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <HiOutlineTrendingUp className="w-4 h-4 lg:w-5 lg:h-5 text-purple-600" />
              </div>
              <div className="min-w-0">
                <div className="text-sm sm:text-base lg:text-lg font-bold text-gray-900">{summary.totalConverted || 0}</div>
                <div className="text-xs text-gray-500">Converted</div>
              </div>
            </div>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <HiOutlineClock className="w-4 h-4 lg:w-5 lg:h-5 text-orange-600" />
              </div>
              <div className="min-w-0">
                <div className="text-sm sm:text-base lg:text-lg font-bold text-gray-900">{formatWorkingHours(summary.totalWorkingHours)}</div>
                <div className="text-xs text-gray-500">Hours</div>
              </div>
            </div>
          </Card>
        </div>

        {/* User Reports Table */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : users.length === 0 ? (
          <Card>
            <EmptyState
              icon={<HiOutlineUser className="w-8 h-8 text-gray-400" />}
              title="No reports found"
              description="No telecallers found for the selected date"
            />
          </Card>
        ) : (
          <Card padding="p-0">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHead>
                  <TableRow hoverable={false}>
                    <TableCell header className="min-w-[150px]">Employee</TableCell>
                    <TableCell header className="min-w-[90px]">Attendance</TableCell>
                    <TableCell header className="min-w-[80px]">Working Hours</TableCell>
                    <TableCell header className="min-w-[80px]">Calls</TableCell>
                    <TableCell header className="min-w-[120px]">Connection Rate</TableCell>
                    <TableCell header className="min-w-[80px]">Leads Worked</TableCell>
                    <TableCell header className="min-w-[80px]">Converted</TableCell>
                    <TableCell header className="min-w-[80px]">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.userId}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-primary-600 font-medium text-sm">
                              {user.name?.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">{user.name}</p>
                            <p className="text-xs text-gray-500 truncate">{user.department}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge variant={getStatusBadge(user.attendance.status)} size="sm">
                            {user.attendance.status}
                          </Badge>
                          {user.attendance.isLate && (
                            <span className="text-xs text-yellow-600 whitespace-nowrap">Late</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-600 whitespace-nowrap">
                          {formatWorkingHours(user.attendance.workingHours)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-900">{user.calls.total}</span>
                          <span className="text-xs text-green-600 whitespace-nowrap">{user.calls.connected} connected</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-12 lg:w-16 h-2 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
                            <div
                              className="h-full bg-green-500 rounded-full"
                              style={{ width: `${user.calls.connectionRate}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-600 whitespace-nowrap">{user.calls.connectionRate}%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-900">{user.leads.total}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-green-600 font-medium">{user.leads.converted}</span>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-1.5"
                          icon={<HiOutlineEye className="w-4 h-4" />}
                          onClick={() => handleViewUser(user.userId)}
                          title="View Details"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {users.map((user) => (
                <div
                  key={user.userId}
                  className="p-4 hover:bg-gray-50 cursor-pointer active:bg-gray-100 transition-colors"
                  onClick={() => handleViewUser(user.userId)}
                >
                  {/* Header Row */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-primary-600 font-semibold text-sm">
                          {user.name?.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">{user.name}</p>
                        <p className="text-xs text-gray-500 truncate">{user.department}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Badge variant={getStatusBadge(user.attendance.status)} size="sm">
                        {user.attendance.status}
                      </Badge>
                      {user.attendance.isLate && (
                        <span className="text-xs text-yellow-600 font-medium">Late</span>
                      )}
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm mb-3">
                    <div className="flex items-center gap-2">
                      <HiOutlineClock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-700">{formatWorkingHours(user.attendance.workingHours)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <HiOutlinePhone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-700">{user.calls.total} calls</span>
                      <span className="text-green-600 text-xs">({user.calls.connected} conn)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <HiOutlineClipboardList className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-700">{user.leads.total} leads</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <HiOutlineTrendingUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-green-600 font-medium">{user.leads.converted} converted</span>
                    </div>
                  </div>

                  {/* Connection Rate Bar */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 rounded-full transition-all duration-300"
                        style={{ width: `${user.calls.connectionRate}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-600 flex-shrink-0">{user.calls.connectionRate}%</span>
                  </div>

                  {/* View Details Indicator */}
                  <div className="flex items-center justify-end mt-2">
                    <span className="text-xs text-primary-600 font-medium flex items-center gap-1">
                      View Details
                      <HiOutlineEye className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* User Detail Modal */}
        <Modal
          isOpen={showUserModal}
          onClose={() => {
            setShowUserModal(false);
            setSelectedUserId(null);
          }}
          title={userReport?.user?.name || 'User Report'}
          size="lg"
        >
          {userLoading ? (
            <div className="flex justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : userReport ? (
            <div className="space-y-3 sm:space-y-4">
              {/* User Info - Sticky Header */}
              <div className="sticky top-0 z-20 flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-xl border-b border-gray-200 shadow-sm">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-primary-600 font-medium text-base sm:text-lg">
                    {userReport.user?.name?.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{userReport.user?.name}</p>
                  <p className="text-xs sm:text-sm text-gray-500 truncate">{userReport.user?.email}</p>
                  <p className="text-xs text-gray-400 truncate">{userReport.user?.department} - {userReport.user?.designation}</p>
                </div>
                <Badge variant={getStatusBadge(userReport.attendance?.status)} size="sm">
                  {userReport.attendance?.status || 'Absent'}
                </Badge>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
                <div className="text-center p-2.5 sm:p-3 bg-blue-50 rounded-xl">
                  <div className="text-base sm:text-lg lg:text-xl font-bold text-blue-600">{userReport.callStats?.total || 0}</div>
                  <div className="text-xs text-blue-600">Total Calls</div>
                </div>
                <div className="text-center p-2.5 sm:p-3 bg-green-50 rounded-xl">
                  <div className="text-base sm:text-lg lg:text-xl font-bold text-green-600">{userReport.callStats?.connected || 0}</div>
                  <div className="text-xs text-green-600">Connected</div>
                </div>
                <div className="text-center p-2.5 sm:p-3 bg-purple-50 rounded-xl">
                  <div className="text-base sm:text-lg lg:text-xl font-bold text-purple-600">{userReport.callStats?.connectionRate || 0}%</div>
                  <div className="text-xs text-purple-600">Rate</div>
                </div>
                <div className="text-center p-2.5 sm:p-3 bg-orange-50 rounded-xl">
                  <div className="text-base sm:text-lg lg:text-xl font-bold text-orange-600">{formatWorkingHours(userReport.attendance?.workingHours)}</div>
                  <div className="text-xs text-orange-600">Hours</div>
                </div>
              </div>

              {/* Attendance Details */}
              {userReport.attendance && (
                <div className="p-3 sm:p-4 border border-gray-200 rounded-xl">
                  <h4 className="font-medium text-gray-900 mb-2 sm:mb-3">Attendance Details</h4>
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm">
                    <div>
                      <span className="text-gray-500">Check In:</span>
                      <span className="ml-1 sm:ml-2 font-medium">
                        {userReport.attendance.checkInTime
                          ? format(new Date(userReport.attendance.checkInTime), 'h:mm a')
                          : '-'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Check Out:</span>
                      <span className="ml-1 sm:ml-2 font-medium">
                        {userReport.attendance.checkOutTime
                          ? format(new Date(userReport.attendance.checkOutTime), 'h:mm a')
                          : '-'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Status:</span>
                      <Badge variant={getStatusBadge(userReport.attendance.status)} size="sm" className="ml-1 sm:ml-2">
                        {userReport.attendance.status}
                      </Badge>
                    </div>
                    <div>
                      <span className="text-gray-500">Leads:</span>
                      <span className="ml-1 sm:ml-2 font-medium">{userReport.allocation?.leadsAllocated || 0}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Hourly Call Breakdown Chart (9 AM - 9 PM IST) */}
              {userReport.hourlyBreakdown && userReport.hourlyBreakdown.length > 0 && (
                <div className="p-3 sm:p-4 border border-gray-200 rounded-xl relative z-0 overflow-hidden">
                  <h4 className="font-medium text-gray-900 mb-2 sm:mb-3">Hourly Call Breakdown (9 AM - 9 PM IST)</h4>
                  <div className="h-48 sm:h-56 relative">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={userReport.hourlyBreakdown}
                        margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis
                          dataKey="label"
                          tick={{ fontSize: 10, fill: '#6b7280' }}
                          axisLine={{ stroke: '#e5e7eb' }}
                          tickLine={false}
                          interval={0}
                          angle={-45}
                          textAnchor="end"
                          height={50}
                        />
                        <YAxis
                          tick={{ fontSize: 10, fill: '#6b7280' }}
                          axisLine={{ stroke: '#e5e7eb' }}
                          tickLine={false}
                          allowDecimals={false}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            fontSize: '12px',
                            position: 'relative',
                            zIndex: 50,
                          }}
                          formatter={(value, name) => [
                            value,
                            name === 'total' ? 'Total Calls' : 'Connected'
                          ]}
                          labelFormatter={(label) => `Time: ${label}`}
                        />
                        <Bar
                          dataKey="total"
                          name="total"
                          fill="#4F46E5"
                          radius={[4, 4, 0, 0]}
                        />
                        <Bar
                          dataKey="connected"
                          name="connected"
                          fill="#22c55e"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex items-center justify-center gap-4 mt-2">
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-sm bg-primary-600"></div>
                      <span className="text-xs text-gray-500">Total Calls</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-sm bg-green-500"></div>
                      <span className="text-xs text-gray-500">Connected</span>
                    </div>
                  </div>
                  {/* Summary Stats */}
                  <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-gray-100">
                    <div className="text-center">
                      <div className="text-sm font-bold text-gray-900">
                        {userReport.hourlyBreakdown.reduce((sum, h) => sum + h.total, 0)}
                      </div>
                      <div className="text-xs text-gray-500">Total</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-bold text-green-600">
                        {userReport.hourlyBreakdown.reduce((sum, h) => sum + h.connected, 0)}
                      </div>
                      <div className="text-xs text-gray-500">Connected</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-bold text-primary-600">
                        {(() => {
                          const total = userReport.hourlyBreakdown.reduce((sum, h) => sum + h.total, 0);
                          const connected = userReport.hourlyBreakdown.reduce((sum, h) => sum + h.connected, 0);
                          return total > 0 ? Math.round((connected / total) * 100) + '%' : '0%';
                        })()}
                      </div>
                      <div className="text-xs text-gray-500">Rate</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Calls */}
              {userReport.callLogs && userReport.callLogs.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2 sm:mb-3">Recent Calls ({userReport.callLogs.length})</h4>
                  <div className="max-h-40 sm:max-h-48 overflow-y-auto space-y-2">
                    {userReport.callLogs.slice(0, 10).map((call, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 sm:p-2.5 bg-gray-50 rounded-lg text-xs sm:text-sm">
                        <div className="min-w-0">
                          <span className="font-medium truncate">{call.leadId?.name || call.phoneNumber}</span>
                          <span className="text-xs text-gray-400 ml-1 sm:ml-2">
                            {format(new Date(call.createdAt), 'h:mm a')}
                          </span>
                        </div>
                        <Badge variant={call.status === 'connected' ? 'success' : 'danger'} size="sm">
                          {call.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Leads Worked */}
              {userReport.leadsWorked && userReport.leadsWorked.leads && userReport.leadsWorked.leads.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2 sm:mb-3">Leads Worked ({userReport.leadsWorked.total})</h4>
                  <div className="max-h-40 sm:max-h-48 overflow-y-auto space-y-2">
                    {userReport.leadsWorked.leads.slice(0, 10).map((lead, idx) => {
                      const statusColor = getLeadStatusColor(lead.status);
                      const statusName = getLeadStatusName(lead.status);
                      return (
                        <div key={idx} className="flex items-center justify-between p-2 sm:p-2.5 bg-gray-50 rounded-lg text-xs sm:text-sm">
                          <div className="min-w-0">
                            <span className="font-medium truncate">{lead.name}</span>
                            <span className="text-xs text-gray-400 ml-1 sm:ml-2">{lead.phone}</span>
                          </div>
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${statusName === 'Hot' ? 'bg-red-100 text-red-600' : statusName === 'Warm' ? 'bg-yellow-100 text-yellow-600' : statusName === 'Cold' ? 'bg-blue-100 text-blue-600' : statusName === 'Converted' ? 'bg-green-100 text-green-600' : statusName === 'Closed' ? 'bg-gray-100 text-gray-600' : 'bg-gray-200 text-gray-700'}`}
                          >
                            {statusName}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 sm:gap-3 justify-end pt-3 sm:pt-4 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowUserModal(false);
                    setSelectedUserId(null);
                  }}
                >
                  Close
                </Button>
                {/* <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigate(`/users/${userReport.user?._id}`);
                    setShowUserModal(false);
                  }}
                >
                  View Profile
                </Button> */}
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No data available</p>
          )}
        </Modal>
      </div>
    </Layout>
  );
};

export default DailyWorkReports;